<script setup lang="ts">
  import { ref, computed, watch, onMounted } from 'vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import AutoSavingForm from '@/components/admin/form/AutoSavingForm.vue'
  import type { CustomerCase, PatchedCustomerCaseRequest } from '@/types/generated/types.gen'
  import { useTranslation } from '@/composables/useTranslation'
  import * as yup from 'yup'
  import { CasesService } from '@/types/generated/services.gen'
  import type { GenericObject } from 'vee-validate'
  import { useFormWithValidation } from '@/composables/useFormWithValidation'
  import InputField from '@/components/admin/form/InputField.vue'

  const props = defineProps<{
    caseData: CustomerCase
    debounceDelay?: number
    throttleInterval?: number
  }>()

  const emit = defineEmits(['update:payload'])

  const { t } = useTranslation({
    en: {
      clerk: 'Clerk',
      assistant: 'Assistant',
      actaportSection: 'Actaport Information',
      clerkRequired: 'Clerk is required',
      assistantRequired: 'Assistant is required',
      title: 'Title',
      titleRequired: 'Title is required'
    },
    de: {
      clerk: 'Sachbearbeiter',
      assistant: 'Assistent',
      actaportSection: 'Actaport Information',
      clerkRequired: 'Sachbearbeiter ist erforderlich',
      assistantRequired: 'Assistent ist erforderlich',
      title: 'Aktenbezeichnung',
      titleRequired: 'Aktenbezeichnung ist erforderlich'
    }
  })

  const defaultTitle = computed(() => {
    const { law, client, opponent } = props.caseData.data

    if (law.field === 'Strafrecht') {
      return `${client.first_name} ${client.last_name}`
    }

    const clientName =
      client.is_company && client.company_name
        ? `${client.company_name} ${client.company_legal_form || 'GmbH'}`
        : client.last_name

    if (!opponent) {
      return `${clientName} ./.`
    }

    const opponentName =
      opponent.is_company && opponent.company_name
        ? `${opponent.company_name} ${opponent.company_legal_form || 'GmbH'}`
        : opponent.last_name

    return `${clientName} ./. ${opponentName}`
  })

  watch(
    () => defaultTitle.value,
    (newValue) => {
      formData.title = newValue
    },
    { deep: true }
  )

  onMounted(() => {
    handleSave({
      title: defaultTitle.value,
      clerk: actaportClerkOptions.value[0]?.value || '',
      assistant: actaportAssistantOptions.value[0]?.value || ''
    })
  })
  const actaportClerkOptions = computed(() =>
    (props.caseData.org.actaport_clerk_options || []).map((option: { id: any; name: any }) => ({
      value: option.id,
      title: option.name
    }))
  )

  const actaportAssistantOptions = computed(() =>
    (props.caseData.org.actaport_assistant_options || []).map((option: { id: any; name: any }) => ({
      value: option.id,
      title: option.name
    }))
  )

  const schema = {
    clerk: yup.string().required(t('clerkRequired')),
    assistant: yup.string().required(t('assistantRequired')),
    title: yup.string().required(t('titleRequired'))
  }

  interface FormData {
    clerk: string
    assistant: string
    title: string
  }

  const {
    formData,
    formErrors,
    touchedFields,
    validate,
    handleFieldTouch,
    handleFieldChange,
    showErrors
  } = useFormWithValidation<FormData>(
    {
      clerk: actaportClerkOptions.value[0]?.value || '',
      assistant: actaportAssistantOptions.value[0]?.value || '',
      title: defaultTitle.value
    },
    schema
  )

  defineExpose({
    validate,
    showErrors
  })

  const handleSave = async (updatedData: GenericObject) => {
    emit('update:payload', updatedData)
  }

  watch(
    formData,
    (newValue) => {
      handleSave(newValue)
    },
    { deep: true }
  )
</script>

<template>
  <div class="rounded-xl border bg-white p-6 shadow-md">
    <h2 class="mb-4 text-2xl font-bold">{{ t('actaportSection') }}</h2>
    <AutoSavingForm
      :debounce-delay="debounceDelay"
      :throttle-interval="throttleInterval"
      class="space-y-6"
      @save="handleSave"
    >
      <div class="space-y-4">
        <div>
          <InputField
            v-model="formData.title"
            :label="t('title')"
            name="title"
            :error="formErrors?.value?.title ?? null"
            :rules="yup.string().required(t('titleRequired'))"
            @update:model-value="(value: any) => handleFieldChange('title', value)"
            @focus="() => handleFieldTouch('title')"
          />
        </div>
        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700">
            {{ t('clerk') }}
          </label>
          <SelectField
            v-model="formData.clerk"
            :options="actaportClerkOptions"
            name="clerk"
            :placeholder="t('clerk')"
            :is-filterable="true"
            class="w-full"
            :error="formErrors.value?.clerk ?? null"
            @update:model-value="(value: any) => handleFieldChange('clerk', value)"
            @focus="() => handleFieldTouch('clerk')"
          />
        </div>
        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700">
            {{ t('assistant') }}
          </label>
          <SelectField
            v-model="formData.assistant"
            :options="actaportAssistantOptions"
            name="assistant"
            :placeholder="t('assistant')"
            :is-filterable="true"
            class="w-full"
            :error="formErrors.value?.assistant ?? null"
            @update:model-value="(value: any) => handleFieldChange('assistant', value)"
            @focus="() => handleFieldTouch('assistant')"
          />
        </div>
      </div>
    </AutoSavingForm>
  </div>
</template>
